/**
 * What the gallery takes, checked in the browser before an upload starts.
 *
 * The server checks all of it again from the file's first bytes. These checks exist because a body over the
 * server's limit is cut off mid-upload, which reaches the browser as a network error rather than as a
 * message, and because nobody on a slow connection should wait for 100 MB to be refused. The caps are the
 * server's own, from `useLimits` (Q48).
 */

import type { ClientLimits } from '../api/types';

/**
 * For the file input: what a camera roll may offer that the gallery accepts. Not HEIC: the server cannot read it, so
 * cannot take the phone's location out of it or print the viewer's name into it (Q78), and most browsers cannot show
 * it. An iPhone hands over a JPEG when the input does not ask for HEIC.
 */
export const GALLERY_ACCEPT = 'image/jpeg,image/png,image/webp,video/mp4,video/webm';

/** Whether a chosen file is a video. The type can be empty on some phones, so the name is a fallback. */
export function isVideoFile(file: Pick<File, 'type' | 'name'>): boolean {
  return file.type.startsWith('video/') || /\.(mp4|m4v|webm)$/i.test(file.name);
}

/** Which size limit a chosen file breaks, if any. */
export function sizeProblem(
  file: Pick<File, 'type' | 'name' | 'size'>,
  limits: Pick<ClientLimits['media'], 'photoMaximumBytes' | 'videoMaximumBytes'>,
): 'photo' | 'video' | null {
  if (isVideoFile(file)) return file.size > limits.videoMaximumBytes ? 'video' : null;
  return file.size > limits.photoMaximumBytes ? 'photo' : null;
}

/**
 * A video's length in milliseconds, read from its metadata by the browser.
 *
 * Null where the browser cannot read it, or reports no finite length: the upload still goes ahead, and the
 * player shows the length itself once it has loaded.
 */
export function measureVideoDuration(file: Blob): Promise<number | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    const finish = (value: number | null) => {
      URL.revokeObjectURL(url);
      resolve(value);
    };

    video.preload = 'metadata';
    video.onloadedmetadata = () =>
      finish(Number.isFinite(video.duration) ? Math.round(video.duration * 1000) : null);
    video.onerror = () => finish(null);
    video.src = url;
  });
}
